import { inject, Injectable } from '@angular/core';
import { Resolution } from '@api-types/enums/resolution.enum';
import {
  areTwoStringArraysEqual,
  getFactorColumn,
} from '@api-types/helper/helper';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { ConfigurationsActions } from 'demand-forecasting/store/configurations/configurations.actions';
import { debounce, filter, map, timer, withLatestFrom } from 'rxjs';

import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import { FilterActions } from 'demand-forecasting/store/filter/filter.actions';
import {
  selectAllFilterCombinationForecastSeries,
  selectPendingFilterCombinationSeriesIds,
  selectSavedFilterCombinations,
  selectSelectedFilterCombinationIdsFromUrl,
} from 'demand-forecasting/store/filter/filter.selectors';
import { ForecastSeriesActions } from 'demand-forecasting/store/forecast-series/forecast-series.actions';
import { latestCompletedForecastSeries } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { ForecastVersionActions } from 'demand-forecasting/store/forecast-version/forecast-version.actions';
import {
  selectCreateVersionIsPending,
  selectForecastVersionByQueryParam,
} from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import {
  selectConfigurationId,
  selectResolution,
  selectTargetUnit,
  selectVersionId,
} from './router-state/router.selectors';

@Injectable()
export class DemandForecastStateEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  private readonly versionId = this.store.selectSignal(selectVersionId);
  private readonly createVersionIsPending = this.store.selectSignal(
    selectCreateVersionIsPending,
  );

  ensureForecastSeriesGetFetchedOnForecastVersionChange = createEffect(
    () =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        withLatestFrom(this.store.select(latestCompletedForecastSeries)),
        // case: forecast series is not loaded at all - fetch it
        filter(([, forecastSeries]) => {
          const versionId = this.versionId();
          if (!forecastSeries && versionId) {
            this.store.dispatch(
              ForecastVersionActions.onForecastVersionChanged({
                newVersionId: versionId,
              }),
            );

            return false;
          }
          return true;
        }),
        // case: forecast series is loaded and we do not want to refetch if the versionId is the same
        filter(([action, versionId]) => {
          const state = action.payload.routerState;
          const { versionId: urlVersionId } = state.root.queryParams;
          return versionId?.resultSetId !== urlVersionId;
        }),
        // case: forecast series is loaded, and we want to refetch if the versionId is different
        map(() => {
          const versionId = this.versionId();
          if (!versionId) {
            return null;
          }
          this.store.dispatch(
            ForecastVersionActions.onForecastVersionChanged({
              newVersionId: versionId,
            }),
          );

          return versionId;
        }),
      ),
    { dispatch: false },
  );

  ensureConfigurationInUrlGetsFetched = createEffect(
    () =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        withLatestFrom(
          this.store.select(ConfigurationsSelectors.selectAllConfigurations),
          this.store.select(selectConfigurationId),
        ),
        map(([, configurations, configurationId]) => {
          // case: url does not contain configurationId
          if (!configurationId) {
            this.store.dispatch(ConfigurationsActions.getAllConfigurations());
            return;
          }
          // check if configurationId is already in store
          if (!configurations.find((config) => config.id === configurationId)) {
            // case configurationId is not in store
            // fetch configuration
            this.store.dispatch(
              ConfigurationsActions.getConfiguration({ configurationId }),
            );
            // load version when acessing the page via direct link
          }
          this.store.dispatch(
            ForecastVersionActions.getAllForecastVersionsForCofiguration({
              configurationId,
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  /**
   * Runs updateForecastSeries action when one of the following url params changes:
   * - resolution
   * - factorColumn
   * - extraForecasts
   */
  updateForecastSeriesOnUrlParamChanges = createEffect(
    () =>
      this.actions$.pipe(
        ofType(routerNavigatedAction),
        withLatestFrom(
          this.store.select(selectResolution),
          this.store.select(
            ConfigurationsSelectors.selectAdditionalTimeSeriesNamesByUrlParam,
          ),
          this.store.select(selectTargetUnit),
          this.store.select(latestCompletedForecastSeries),
          this.store.select(selectVersionId),
          this.store.select(
            ConfigurationsSelectors.selectConfigurationByUrlParam,
          ),
        ),
        map(
          ([
            ,
            resolution,
            additionalTimeSeriesNames,
            targetUnit,
            forecastSeries,
            versionId,
            configuration,
          ]) => {
            // also send options on initial load, therefore we check forecastSeries === undefined on one property to send all options on initial load
            const isInitialLoad = forecastSeries === undefined;
            const additionalTimeSeriesSelectionChanged =
              forecastSeries !== undefined &&
              !areTwoStringArraysEqual(
                additionalTimeSeriesNames,
                Object.keys(forecastSeries.additionalTimeSeries),
              );
            const resolutionChanged =
              forecastSeries !== undefined &&
              resolution !== forecastSeries.resolution;

            // TODO check, this might not be working as expected
            const targetUnitChanged = targetUnit !== forecastSeries?.yAxis;

            if (
              configuration &&
              (isInitialLoad ||
                targetUnitChanged ||
                resolutionChanged ||
                additionalTimeSeriesSelectionChanged)
            ) {
              const debounceDelay = additionalTimeSeriesSelectionChanged
                ? 600
                : 0;

              return {
                options: {
                  resolution,
                  factorColumn: getFactorColumn(configuration, targetUnit),
                  additionalTimeSeries: additionalTimeSeriesNames,
                },
                versionId,
                debounceDelay,
                isInitialLoad,
                loadedVersionId: forecastSeries?.resultSetId,
              };
            }

            return {
              options: null,
              debounceDelay: 0,
              isInitialLoad,
              versionId,
              loadedVersionId: forecastSeries?.resultSetId,
            };
          },
        ),
        debounce((data) => timer(data.debounceDelay)),
        map((data) => {
          const { versionId, isInitialLoad, options, loadedVersionId } = data;
          const createVersionIsPending = this.createVersionIsPending();

          if (!isInitialLoad && options && !createVersionIsPending) {
            this.store.dispatch(
              ForecastSeriesActions.applyForecastSeriesOptions({
                options: {
                  resolution: options.resolution as Resolution,
                  factorColumn: options.factorColumn,
                  additionalTimeSeriesNames: options.additionalTimeSeries,
                },
              }),
            );
          } else if (versionId && versionId !== loadedVersionId) {
            // case: forecast version actually changed
            this.store.dispatch(
              ForecastSeriesActions.getForecastSeriesByVersionId({
                versionId,
              }),
            );
          }
        }),
      ),
    { dispatch: false },
  );

  updateFilterCombinationForecastSeriesOnUrlParamChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          routerNavigatedAction,
          FilterActions.setSavedFilterCombinations,
          FilterActions.refetchFilterCombinationForecastSeriesOnConfigurationChange,
          ForecastSeriesActions.setForecastSeries,
        ),
        withLatestFrom(
          this.store.select(
            selectSelectedFilterCombinationIdsFromUrl('filterCombinationIds'),
          ),
          this.store.select(selectAllFilterCombinationForecastSeries),
          this.store.select(selectSavedFilterCombinations),
          this.store.select(selectForecastVersionByQueryParam),
          this.store.select(selectPendingFilterCombinationSeriesIds),
          this.store.select(latestCompletedForecastSeries),
        ),
        map(
          ([
            ,
            selectedFilterCombinationIds,
            filterCombinationForecastSeries,
            savedFilterCombinations,
            version,
            pendingFilterCombinationSeriesIds,
            forecastSeries,
          ]) => {
            if (
              savedFilterCombinations.length < 1 ||
              !version ||
              !forecastSeries
            )
              return;
            // we want to fetch all filterCombinationForecastSeries that are in the url
            // in case we have a filterCombinations that is not in the url anymore, we want to remove it

            // remove all that are not in the url anymore
            Object.keys(filterCombinationForecastSeries).forEach(
              (filterCombinationId) => {
                if (
                  !selectedFilterCombinationIds.includes(filterCombinationId)
                ) {
                  this.store.dispatch(
                    FilterActions.removeFilterCombinationForecastSeries({
                      filterCombinationId,
                    }),
                  );
                }
              },
            );

            // in case a filter combination series is not in the store, we want to check if it is already pending
            // if not, we want to fetch it
            selectedFilterCombinationIds.forEach((filterCombinationId) => {
              if (!filterCombinationForecastSeries[filterCombinationId]) {
                if (
                  !pendingFilterCombinationSeriesIds.includes(
                    filterCombinationId,
                  )
                ) {
                  this.store.dispatch(
                    FilterActions.getFilterCombinationForecastSeries({
                      filterCombinationId,
                    }),
                  );
                }
              }
            });
          },
        ),
      ),
    { dispatch: false },
  );
}
